/**
 * The desktop half of "this HTTP response is an event stream" — see
 * `src-tauri/src/http_stream.rs` for the events and `utils/httpEventStreams.js` for where
 * they end up.
 *
 * The listeners are attached BEFORE the request is sent: the backend starts forwarding the
 * body the moment the head is in, and an event emitted before `listen()` has landed is lost.
 * They come off when the stream ends, breaks, is CANCELLED (the backend aborts silently — no
 * done / error follows a cancel), or turned out not to be one.
 */
import { invoke } from '@tauri-apps/api/core';

const detachers = new Map(); // stream id → take its listeners off

/**
 * @param {{ id: string, onOpen?: Function, onChunk?: Function, onDone?: Function,
 *           onError?: Function, onNotAStream?: Function }} stream
 * @returns {Promise<{ settle: (response: object | null) => void }>} call `settle` with the
 *          response (or `null` when the request failed)
 */
export async function attachHttpStream(stream) {
  // loaded when a stream is asked for: `utils/tauri.js` is imported by half the app (and its
  // tests), the event API only matters here
  const { listenSafe } = await import('./safeTauriEvent');
  const offs = [];
  let detached = false;
  const detach = () => { detached = true; detachers.delete(stream.id); while (offs.length) offs.pop()(); };
  // a cancel can land WHILE the listeners are still being attached: what attaches after it comes straight off
  const keep = (off) => { if (detached) off(); else offs.push(off); };
  detachers.set(stream.id, detach);
  try {
    keep(await listenSafe(`http-stream-chunk-${stream.id}`, (evt) => stream.onChunk?.(evt.payload)));
    keep(await listenSafe(`http-stream-done-${stream.id}`, () => { detach(); stream.onDone?.(); }));
    keep(await listenSafe(`http-stream-error-${stream.id}`, (evt) => { detach(); stream.onError?.(evt.payload); }));
  } catch (error) {
    detach(); // half-attached is not attached
    stream.onNotAStream?.();
    throw error;
  }
  return {
    settle: (response) => {
      if (response?.streaming) {
        if (stream.onOpen?.() === false) { detach(); cancelHttpStream(stream.id); }
        return;
      }
      detach();
      stream.onNotAStream?.();
    },
  };
}

/** A real stop: the backend drops the response, which closes the connection. */
export function cancelHttpStream(streamId) {
  detachers.get(streamId)?.();
  return invoke('http_stream_cancel', { streamId }).catch(() => false);
}
